// TODO: note
// when changing/fixing implementation fix also web-components and o2-components implementation

export type Attributes = { [key: string]: any };

/**
 * Returns attributes object defined in HTML element. Mainly serves for moving aria-* attributes from Host element to its native element inside.
 * Function should be called in connectedCallback and assigned to a variable, that is later used in the render function.
 *
 * This does not need to be reactive as changing attributes on the host element
 * does not trigger a re-render.
 */
const inheritAttributes = (
  el: HTMLElement,
  attributes: string[] = [],
) => {
  const attributeObject: Attributes = {};
  attributes.forEach((attr) => {
    const value = el.getAttribute(attr);
    if (value !== null) {
      attributeObject[attr] = value;
      el.removeAttribute(attr);
    }
  });
  return attributeObject;
};

/**
 * List of available ARIA attributes and 'role':
 * https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes
 */
const ariaAttributes = [
  'role',
  'aria-activedescendant',
  'aria-atomic',
  'aria-autocomplete',
  'aria-braillelabel',
  'aria-brailleroledescription',
  'aria-busy',
  'aria-checked',
  'aria-colcount',
  'aria-colindex',
  'aria-colindextext',
  'aria-colspan',
  'aria-controls',
  'aria-current',
  'aria-describedby',
  'aria-description',
  'aria-details',
  'aria-disabled',
  'aria-errormessage',
  'aria-expanded',
  'aria-flowto',
  'aria-haspopup',
  'aria-hidden',
  'aria-invalid',
  'aria-keyshortcuts',
  'aria-label',
  'aria-labelledby',
  'aria-level',
  'aria-live',
  'aria-multiline',
  'aria-multiselectable',
  'aria-orientation',
  'aria-owns',
  'aria-placeholder',
  'aria-posinset',
  'aria-pressed',
  'aria-readonly',
  'aria-relevant',
  'aria-required',
  'aria-roledescription',
  'aria-rowcount',
  'aria-rowindex',
  'aria-rowindextext',
  'aria-rowspan',
  'aria-selected',
  'aria-setsize',
  'aria-sort',
  'aria-valuemax',
  'aria-valuemin',
  'aria-valuenow',
  'aria-valuetext',
  'focusable',
];

/**
 * Returns an array of aria attributes that should be copied from
 * the host element to a native element inside the Host.
 * @param el The element that the attributes should be copied from.
 * @param ignoreList The list of aria-attributes to ignore reflecting and removing from the host.
 * Use this in instances where we manually specify aria attributes on the `<Host>` element.
 */

export const inheritAriaAttributes = (
  el: HTMLElement,
  ignoreList: string[] = [],
) => {
  let attributesToInherit = ariaAttributes.filter(
    (attr) => !ignoreList.includes(attr),
  );

  return inheritAttributes(el, attributesToInherit);
};

/**
 * Validates required attributes for accessibility or functionality.
 * @param componentName - Name of the component for warnings.
 * @param attributes - Attributes to check.
 * @param options - Specifies required attributes.
 */
export const assertAttributeRequirements = (
  componentName: string,
  attributes: { [key: string]: any },
  options: {
    requiredOneOf?: string[];
    requiredAll?: string[];
  },
) => {
  const isSet = (attr: any) => attr != null && attr !== '';

  if (options.requiredOneOf) {
    const hasOneOf = options.requiredOneOf.some((attr) =>
      isSet(attributes[attr]),
    );
    if (!hasOneOf) {
      console.warn(
        `[${componentName}] warning: At least one of ${options.requiredOneOf.join(' or ')} should be set.`,
      );
    }
  }

  if (options.requiredAll) {
    const hasAll = options.requiredAll.every((attr) => isSet(attributes[attr]));
    if (!hasAll) {
      console.warn(
        `[${componentName}] warning: All of ${options.requiredAll.join(' and ')} should be set.`,
      );
    }
  }
};

/**
 * Observes changes to ARIA attributes on a given element and triggers a callback
 * when any of the specified attributes are modified.
 *
 * @param element The DOM element to observe for ARIA attribute changes.
 * @param callback The callback function to be executed when any of the ARIA attributes change.
 *
 * @returns MutationObserver instance that is observing the element for changes.
 */
export const observeAriaAttributes = (
  element: HTMLElement,
  callback: () => void,
): MutationObserver => {
  const observer = new MutationObserver(() => {
    callback();
  });

  observer.observe(element, {
    attributes: true,
    attributeFilter: ariaAttributes,
  });

  return observer;
};
